/** Add-entity form for Entity Structure (QOE-RECON-1 — extracted from
 *  EntityStructurePage.tsx to clear the FILE_SIZE_POLICY.md 400-line
 *  threshold). Pure presentation: all validation, the create-entity API
 *  call, and the post-save refetch stay in the parent page. */
import type { ConsolidationMethod, DealEntity } from '@/api/client'

export interface AddEntityFormState {
  name: string
  entity_type: string
  ownership_pct: number
  consolidation_method: ConsolidationMethod
  is_primary: boolean
  notes: string
  carved_out: boolean
  guarantees_debt: boolean
  exists_to_serve_group: boolean
  parent_entity_id: string
}

export const DEFAULT_ENTITY_FORM: AddEntityFormState = {
  name: '',
  entity_type: 'LLC',
  ownership_pct: 100,
  consolidation_method: 'auto',
  is_primary: false,
  notes: '',
  carved_out: false,
  guarantees_debt: false,
  exists_to_serve_group: false,
  parent_entity_id: '',
}

export default function AddEntityForm({
  form,
  setForm,
  formError,
  saving,
  entities,
  onSave,
  onCancel,
}: {
  form: AddEntityFormState
  setForm: (updater: (f: AddEntityFormState) => AddEntityFormState) => void
  formError: string
  saving: boolean
  entities: DealEntity[]
  onSave: () => void
  onCancel: () => void
}) {
  return (
    <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
      <h2 className="font-medium text-gray-800">Add Legal Entity</h2>
      {formError && (
        <p className="text-sm text-red-600">{formError}</p>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Entity Name *</label>
          <input
            type="text"
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder="Lone Star HVAC Services LLC"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Entity Type</label>
          <select
            value={form.entity_type}
            onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            {['LLC', 'S-Corp', 'C-Corp', 'LP', 'LLP', 'Sole Prop', 'Trust', 'Other'].map(t => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ownership %</label>
          <input
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={form.ownership_pct}
            onChange={e => setForm(f => ({ ...f, ownership_pct: parseFloat(e.target.value) || 0 }))}
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Consolidation Method</label>
          <select
            value={form.consolidation_method}
            onChange={e =>
              setForm(f => ({ ...f, consolidation_method: e.target.value as ConsolidationMethod }))
            }
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="auto">Auto (ASC 810 hierarchy)</option>
            <option value="full">Full consolidation (100% + NCI)</option>
            <option value="equity">Equity method (one line)</option>
            <option value="cost">Cost / fair value</option>
          </select>
          {/* CONSOL-FIX-1 C-B — pro-rata is deliberately absent. It is an
              IFRS joint-arrangement concept, not a US GAAP option for a
              controlled sub; the API rejects it with a 422. */}
          <p className="mt-1 text-xs text-gray-500">
            Leave on Auto unless you have a reason. The engine applies the
            ASC 810 hierarchy — control consolidates in full, significant
            influence goes to the equity method.
          </p>
        </div>
        <div className="flex items-center gap-2 pt-4">
          <input
            type="checkbox"
            id="is_primary"
            checked={form.is_primary}
            onChange={e => setForm(f => ({ ...f, is_primary: e.target.checked }))}
            className="rounded"
          />
          <label htmlFor="is_primary" className="text-sm text-gray-700">Primary Entity</label>
        </div>
        {/* FIELDS-1 P2 — deal perimeter. The engine has always been able to
            treat an entity as carved out, but nothing could set the flag, so
            rent paid to an owner's retained real-estate LLC was eliminated
            as an intercompany wash instead of being called out as a real
            cost the buyer inherits. */}
        <div className="flex items-start gap-2 pt-4">
          <input
            type="checkbox"
            id="carved_out"
            checked={form.carved_out}
            onChange={e => setForm(f => ({ ...f, carved_out: e.target.checked }))}
            className="mt-0.5 rounded"
          />
          <label htmlFor="carved_out" className="text-sm text-gray-700">
            Carved out of the deal
            <span className="block text-xs text-gray-500">
              Entity stays with the seller — e.g. a real-estate LLC that keeps
              charging rent. Intercompany transactions with it are flagged as
              real costs, not eliminated.
            </span>
          </label>
        </div>
        {/* CONSOL-FIX-1 C-A — the ASC 810 control facts, asked in plain
            language. The CPA is never made to say "VIE"; these are the two
            questions that decide it, and either one true consolidates the
            entity whatever the ownership box says. Without them the engine
            can only reach the voting-interest test, which is the SECOND
            rung of the hierarchy — an owner-held PropCo the group
            guarantees would drop out of the group figures entirely. */}
        <div className="col-span-2 border-t pt-3 space-y-2">
          <p className="text-xs font-medium text-gray-600">
            Control facts — answer for the entity being added
          </p>
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="guarantees_debt"
              checked={form.guarantees_debt}
              onChange={e => setForm(f => ({ ...f, guarantees_debt: e.target.checked }))}
              className="mt-0.5 rounded"
            />
            <label htmlFor="guarantees_debt" className="text-sm text-gray-700">
              The group guarantees this entity's debt
              <span className="block text-xs text-gray-500">
                Or otherwise absorbs its losses — e.g. the operating company
                signed for the building loan.
              </span>
            </label>
          </div>
          <div className="flex items-start gap-2">
            <input
              type="checkbox"
              id="exists_to_serve_group"
              checked={form.exists_to_serve_group}
              onChange={e =>
                setForm(f => ({ ...f, exists_to_serve_group: e.target.checked }))
              }
              className="mt-0.5 rounded"
            />
            <label htmlFor="exists_to_serve_group" className="text-sm text-gray-700">
              It exists mainly to serve the operating business
              <span className="block text-xs text-gray-500">
                The classic case is a real-estate LLC whose only tenant is
                the operating company.
              </span>
            </label>
          </div>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Parent Entity
          </label>
          <select
            value={form.parent_entity_id}
            onChange={e => setForm(f => ({ ...f, parent_entity_id: e.target.value }))}
            className="w-full border rounded px-2 py-1.5 text-sm"
          >
            <option value="">None — sister entity under common control</option>
            {entities.map(e => (
              <option key={e.entity_id} value={e.entity_id}>{e.name}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Affects the statement label only: a parent-subsidiary group
            produces <em>Consolidated</em> statements, brother-sister
            entities produce <em>Combined</em> ones. The eliminations are
            identical either way.
          </p>
        </div>
        <div className="col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
          <input
            type="text"
            value={form.notes}
            onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            className="w-full border rounded px-2 py-1.5 text-sm"
            placeholder="Optional notes"
          />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={onSave}
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Add Entity'}
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-sm border rounded text-gray-600 hover:bg-gray-100"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
